// src/services/supplierService.js
import { BACKEND_URL } from "../utils/envConfig";
import apiClient from "./apiClient";
import {
  formatSupplierFields,
  normalizeSupplierList,
} from "../utils/nameFormatUtils";

const API_BASE_URL = BACKEND_URL;

// Get all suppliers
export const getAllSuppliers = async () => {
  try {
    // Use apiClient so organizationId is automatically added for hospital org users,
    // which makes the backend use the organization-specific suppliers table.
    const response = await apiClient.get("/suppliers");
    const data = response.data;

    // Support both plain array and wrapped { data: [...] } responses
    if (Array.isArray(data)) {
      return normalizeSupplierList(data);
    }
    if (data && Array.isArray(data.data)) {
      return normalizeSupplierList(data.data);
    }
    return [];
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

// Search suppliers by name
export const searchSuppliers = async (searchTerm) => {
  try {
    // Use apiClient so organizationId is automatically added where applicable
    const response = await apiClient.get("/suppliers/search", {
      params: { name: searchTerm },
    });
    const data = response.data;

    if (Array.isArray(data)) {
      return normalizeSupplierList(data);
    }
    if (data && Array.isArray(data.data)) {
      return normalizeSupplierList(data.data);
    }
    return [];
  } catch (error) {
    console.error('Error searching suppliers:', error);
    throw error;
  }
};

// Get supplier by ID
export const getSupplierById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch supplier');
    }
    
    return formatSupplierFields(data.data);
  } catch (error) {
    console.error('Error fetching supplier:', error);
    throw error;
  }
};

// Create or update supplier
export const createSupplier = async (supplierData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/suppliers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(formatSupplierFields(supplierData))
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create supplier');
    }
    
    // Handle different response structures: { data: {...} } or direct {...}
    // Based on the actual response, return the supplier object
    if (data.data) {
      return formatSupplierFields(data.data); // Response wrapped in data property
    } else if (data.id) {
      return formatSupplierFields(data); // Response is the supplier object directly
    } else {
      // Fallback: return the whole response
      console.warn('[supplierService] Unexpected response structure:', data);
      return formatSupplierFields(data);
    }
  } catch (error) {
    console.error('Error creating supplier:', error);
    throw error;
  }
};

// Update supplier
export const updateSupplier = async (id, supplierData) => {
  try {
    const response = await apiClient.put(`/suppliers/${id}`, formatSupplierFields(supplierData));
    const data = response.data;

    if (data?.data) {
      return formatSupplierFields(data.data);
    }
    if (data?.id) {
      return formatSupplierFields(data);
    }
    return data;
  } catch (error) {
    console.error('Error updating supplier:', error);
    const message =
      error?.response?.data?.message || error?.message || 'Failed to update supplier';
    throw new Error(message);
  }
};

// Delete supplier
export const deleteSupplier = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete supplier');
    }
    
    return data;
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
};

